import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailPlus, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, inp } from "@/components/admin/AdminUi";

const EMPTY = { name: "", email: "", phone: "", is_company: false, company: "", vat: "", sdi: "", address: "", city: "", zip: "", province: "",
  manager_ids: [], price_list_id: "", pay_later: false, admin_notes: "", marketing_consent: false, profiling_consent: false };
const TEXT = [["name", "Nome e cognome *"], ["email", "Email *"], ["phone", "Telefono"], ["company", "Ragione sociale / Azienda"], ["vat", "P.IVA / Codice fiscale"],
  ["sdi", "Codice SDI"], ["address", "Indirizzo"], ["city", "Città"], ["zip", "CAP"], ["province", "Provincia"]];

function Check({ k, f, setF, label, hint }) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" data-testid={`new-customer-${k}`} checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} className="mt-0.5 w-4 h-4 accent-brand-navy" />
      <span>{label}{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
    </label>
  );
}

export function NewCustomerDialog({ open, onOpenChange }) {
  const [f, setF] = useState(EMPTY);
  const [lists, setLists] = useState([]);
  const [managers, setManagers] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    setF(EMPTY);
    api.get("/admin/managers").then((r) => setManagers(r.data));
    api.get("/admin/price-lists").then((r) => setLists(r.data));
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/customers", { ...f, price_list_id: f.price_list_id || null });
      data.email_sent ? toast.success(`Cliente creato: email di conferma inviata a ${f.email}`) : toast.warning("Cliente creato, ma l'email non è partita: usa \"Reinvia email\" nella scheda");
      onOpenChange(false);
      navigate(`/admin/clienti/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const toggleMgr = (id) => setF({ ...f, manager_ids: f.manager_ids.includes(id) ? f.manager_ids.filter((m) => m !== id) : [...f.manager_ids, id] });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-customer-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MailPlus className="w-5 h-5" /> Nuovo cliente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-600">Il cliente riceve un'email con <b>password temporanea</b> e link per confermare iscrizione e privacy (valido 7 giorni). L'account si attiva solo dopo la sua conferma.</p>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="new-customer-is_company" checked={f.is_company} onChange={(e) => setF({ ...f, is_company: e.target.checked })} className="w-4 h-4 accent-brand-navy" /> È un'azienda / negozio</label>
          <div className="grid sm:grid-cols-2 gap-3">
            {TEXT.map(([k, l]) => (
              <Field key={k} label={l}>
                <input data-testid={`new-customer-${k}-input`} required={k === "name" || k === "email"} type={k === "email" ? "email" : "text"} className={inp} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              </Field>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Listino">
              <select data-testid="new-customer-price-list" className={inp} value={f.price_list_id} onChange={(e) => setF({ ...f, price_list_id: e.target.value })}>
                <option value="">Prezzi standard</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Manager">
              <div className="flex flex-wrap gap-1.5">
                {managers.length === 0 && <span className="text-xs text-slate-400">Nessun manager</span>}
                {managers.map((m) => (
                  <button type="button" key={m.id} data-testid={`new-customer-mgr-${m.id}`} onClick={() => toggleMgr(m.id)}
                    className={`press h-8 px-3 rounded-full text-xs font-semibold border ${f.manager_ids.includes(m.id) ? "bg-brand-navy text-white border-brand-navy" : "border-slate-300"}`}>{m.name}</button>
                ))}
              </div>
            </Field>
          </div>
          <div className="rounded-lg bg-slate-50 p-3 space-y-2">
            <div className="text-[11px] font-mono uppercase text-slate-500">Privacy (il cliente le conferma o le cambia dal link)</div>
            <p className="text-xs text-slate-600">Informativa privacy: la accetta il cliente dal link, è obbligatoria per attivare l'account.</p>
            <Check k="marketing_consent" f={f} setF={setF} label="Comunicazioni commerciali e offerte" />
            <Check k="profiling_consent" f={f} setF={setF} label="Offerte personalizzate (profilazione)" />
          </div>
          <Check k="pay_later" f={f} setF={setF} label="Può ordinare senza pagamento immediato" />
          <Field label="Note interne (visibili solo a te)">
            <textarea data-testid="new-customer-notes" rows={2} className={inp + " h-auto py-2"} value={f.admin_notes} onChange={(e) => setF({ ...f, admin_notes: e.target.value })} />
          </Field>
          <button data-testid="new-customer-submit" disabled={busy} className="press w-full h-11 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Crea cliente e invia email
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function InviteBanner({ user, onSent }) {
  const [busy, setBusy] = useState(false);
  if (user.status !== "invited") return null;
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${user.id}/invite`);
      data.email_sent ? toast.success("Nuova email inviata: la password temporanea precedente non vale più") : toast.error("Email non inviata, riprova");
      onSent?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const exp = user.invite_expires_at ? new Date(user.invite_expires_at) : null;
  return (
    <div data-testid="customer-invite-banner" className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="text-sm">
        <b className="text-amber-900">In attesa di conferma del cliente.</b> L'account si attiva quando il cliente clicca il link nell'email.
        {exp && <span className="block text-xs text-amber-800" data-testid="customer-invite-expiry">{exp < new Date() ? "Link scaduto il " : "Link valido fino al "}{exp.toLocaleDateString("it-IT")}</span>}
      </div>
      <button data-testid="customer-invite-resend" onClick={resend} disabled={busy} className="press h-10 px-4 rounded-md bg-brand-navy text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Reinvia email
      </button>
    </div>
  );
}
